<script lang="ts">
  import Button from '$lib/components/Button.svelte';
  import Carousel from '$lib/components/Carousel.svelte';
  import CarouselItem from '$lib/components/CarouselItem.svelte';
  import Head from '$lib/components/Head.svelte';
  import LoadingSpinner from '$lib/components/LoadingSpinner.svelte';
  import PostCard from '$lib/components/Post/PostCard.svelte';
  import { MetaInfo } from '$lib/types/metaInfo';
  import { formatLongDate } from '$lib/utils/localeFormatter';
  import { getBlogMetaTags } from '$lib/utils/metaService';

  export let data: import('./$types').PageData;

  $: post = data.post;
  $: relatedPosts = data.relatedPosts;
  $: user = data.user;

  let metaInfo: MetaInfo;

  $: if (post) {
    isContentProtected = post.excerpt.protected && post.excerpt.rendered === '';

    if (!isContentProtected) {
      const yoast = post.yoast_head_json;
      metaInfo = new MetaInfo(yoast.title, getBlogMetaTags(yoast), yoast.canonical, yoast.schema);
    }
  }

  let isContentProtected: boolean = true;

  let password: string | null;
</script>

{#if !isContentProtected}
  <Head {metaInfo}></Head>
{/if}

{#if data.isLoading}
  <LoadingSpinner></LoadingSpinner>
{/if}

{#if !data.isLoading}
  <div class="container">
    <div class="row single-post-container justify-content-center">
      {#if isContentProtected}
        <div class="col-6">
          <form class="my-5" method="get" action="">
            <div class="form-group mb-2">
              <label class="mb-1" for="password">Content is protected insert the password</label>
              <input
                type="text"
                class="form-control"
                id="password"
                name="password"
                bind:value={password}
                required
              />
            </div>
            <Button label="Submit" buttonType="submit">Submit</Button>
          </form>
        </div>
      {/if}

      {#if !isContentProtected}
        <div class="col-sm-10 mt-5">
          <header class="post-header">
            <h1 class="post-title">{@html post.title.rendered}</h1>
            {#if user}
              <p class="post-meta">
                By <span class="author">{user.name}</span> - {formatLongDate(post.date)}
              </p>
            {/if}
          </header>

          <div class="post-content">
            {@html post.content.rendered}
          </div>
        </div>
      {/if}
    </div>

    {#if relatedPosts.length > 0}
      <div class="row justify-content-center my-5">
        <div class="col-sm-10">
          <h2>Related Posts</h2>

          {#key relatedPosts}
            <Carousel id={'relatedPosts'} numberOfItems={relatedPosts.length}>
              {#each relatedPosts as relatedPost, index}
                <CarouselItem {index} numberOfItems={relatedPosts.length}>
                  <PostCard post={relatedPost} user={relatedPost.yoast_head_json.author} />
                </CarouselItem>
              {/each}
            </Carousel>
          {/key}
        </div>
      </div>
    {/if}
  </div>
{/if}

<style>
  .single-post-container :global(.post-title) {
    font-size: 42px;
  }

  .single-post-container :global(h2.wp-block-heading) {
    margin-top: 20px;
    font-size: 32px;
    color: var(--white);
    margin-bottom: 0.8em;
  }

  .single-post-container :global(h3.wp-block-heading) {
    font-size: 24px;
    margin-bottom: 0.7em;
  }

  .single-post-container :global(p) {
    margin-bottom: 2em;
    line-height: 1.75;
    font-size: 19px;
  }

  .post-content,
  .post-meta {
    font-size: 17px;
    line-height: 1.7;
  }

  .post-content :global(.wp-block-image > img),
  .post-content :global(.wp-block-media-text__media > img),
  .post-content :global(.wp-block-video > video) {
    display: block;
    max-width: 100%;
    margin: 2.5em auto;
    height: auto;
  }

  .post-content :global(.wp-block-table table),
  :global(.wp-block-table th),
  :global(.wp-block-table td) {
    border: 1px solid var(--white);
  }

  .post-content :global(.wp-block-table td) {
    text-align: left;
    padding: 5px;
  }

  .post-content :global(.wp-block-table table) {
    margin: 0 auto;
  }

  .post-content :global(figcaption) {
    text-align: center;
    font-style: italic;
    font-size: 14px;
    color: var(--grey-50);
  }

  .form-control,
  .form-control:focus {
    color: var(--white);
    border-color: var(--white);
    background-color: transparent;
  }

  .post-content :global(.wp-block-quote) {
    font-size: 18px;
    font-style: italic;
    margin: 20px 40px;
    padding: 0.35em 40px;
    line-height: 1.45;
    position: relative;
  }

  .post-content :global(.wp-block-quote:before) {
    font-size: 80px;
    color: var(--white);
    content: '\201C';
    display: block;
    position: absolute;
    padding-left: 10px;
    left: -20px;
    top: -20px;
  }

  .post-content :global(.wp-block-quote:after) {
    font-size: 80px;
    color: var(--white);
    content: '\201D';
    display: block;
    position: absolute;
    right: 0px;
    bottom: -20px;
  }

  .post-content :global(.wp-block-quote > p) {
    font-size: 18px;
    color: var(--grey-60);
    margin-bottom: 5px;
  }

  .post-content :global(.wp-block-quote > cite) {
    font-style: normal;
    font-size: 14px;
    color: var(--grey-60);
    display: block;
    text-align: right;
  }

  .post-content :global(.wp-block-video) {
    max-width: 650px;
    margin: 0 auto;
  }
</style>
